/**
 * Impact system — startle detection from cat-on-cat collisions.
 *
 * Per docs/PLAN.md §4.9:
 *   - lastImpact decays per frame so a fresh impact spike fades naturally.
 *   - Above a small threshold, fire a startled pop animation + audio + haptic.
 *   - Above a large threshold, additionally spike restlessness (milestone 2).
 */

import type { CatState, ImpactEvent } from './types.js';

const IMPACT_DECAY_PER_SECOND = 4.0;
const SMALL_IMPACT_THRESHOLD = 0.6;
const LARGE_IMPACT_THRESHOLD = 2.5;

/**
 * Apply a sim-reported impact event to a cat state.
 * Takes max so a brand-new impact doesn't get added to a still-decaying one.
 */
export function applyImpact(state: CatState, event: ImpactEvent): void {
  state.lastImpactImpulse = Math.max(state.lastImpactImpulse, event.impulse);
}

/**
 * Decay the lastImpactImpulse field over time.
 * Called once per fixed step on every placed cat.
 */
export function decayImpact(state: CatState, dtSeconds: number): void {
  if (state.lastImpactImpulse <= 0) return;
  const next = state.lastImpactImpulse - IMPACT_DECAY_PER_SECOND * dtSeconds;
  state.lastImpactImpulse = Math.max(0, next);
}

export type StartleLevel = 'none' | 'small' | 'large';

export function startleLevelFromImpulse(impulse: number): StartleLevel {
  if (impulse >= LARGE_IMPACT_THRESHOLD) return 'large';
  if (impulse >= SMALL_IMPACT_THRESHOLD) return 'small';
  return 'none';
}
